import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Avatar,
    Box,
    SkeletonCircle,
    SkeletonText,
    useToast,
    Badge,
    Button,
    Tooltip,
    IconButton,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { TimeDifference } from "../../common/TimeDifference";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../common/URLs";
import {
    MapPinIcon,
    ClockIcon,
    CurrencyRupeeIcon,
    BriefcaseIcon,
    SparklesIcon,
    BookmarkIcon as BookmarkOutline,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkSolid, CheckCircleIcon } from "@heroicons/react/24/solid";
import { NoDataFound } from "../../../pages/CommonPublicPages/Basic/NoDataFound";
import { updateBookmarkAsync } from "../../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice";
import { fetchUserRole } from "../../common/FetchUserRole";
import {
    fetchCandidateMostActiveRecentJobsAsync,
    selectCandidateActiveRecentJobs,
} from "./CandidateMostActiveRecentJobSlice";

function CandidateMostActiveRecentJob() {
    const dispatch = useDispatch();
    const jobsData = useSelector(selectCandidateActiveRecentJobs);
    const jobs = Array.isArray(jobsData?.data) ? jobsData.data : [];

    const [isLoading, setIsLoading] = useState(() => jobs.length === 0);
    const [bookmarkedJobs, setBookmarkedJobs] = useState({});
    const [appliedJobs, setAppliedJobs] = useState({});

    // UI controls
    const [sortBy, setSortBy] = useState("recent"); // 'recent' | 'salaryAsc' | 'salaryDesc'
    const [filterBy, setFilterBy] = useState("all"); // 'all' | 'saved' | 'applied'
    const [visibleCount, setVisibleCount] = useState(6);

    const toast = useToast();
    const navigate = useNavigate();

    const hasFetchedData = useRef(false);

    // Fetch once if empty
    useEffect(() => {
        if (jobs.length === 0 && !hasFetchedData.current) {
            hasFetchedData.current = true;
            setIsLoading(true);
            dispatch(fetchCandidateMostActiveRecentJobsAsync())
                .catch((error) => {
                    console.error("Error fetching jobs:", error);
                    toast({
                        title: "Error loading jobs",
                        description: "Could not fetch job recommendations",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                        position: "top-right",
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [dispatch, jobs.length, toast]);

    // Hide loading if jobs exist (e.g., returning from another page)
    useEffect(() => {
        if (jobs.length > 0) {
            setIsLoading(false);
        }
    }, [jobs.length]);

    // Initialize applied + saved states from API whenever jobs change
    useEffect(() => {
        if (Array.isArray(jobs) && jobs.length > 0) {
            const initialApplied = {};
            const initialBookmarks = {};
            jobs.forEach((job) => {
                initialApplied[job.uuid] = job.candidate_job_apply_count === 1;
                initialBookmarks[job.uuid] = (job.candidate_job_saved_count || 0) > 0;
            });
            setAppliedJobs(initialApplied);
            setBookmarkedJobs(initialBookmarks);
        }
    }, [jobs]);

    // Reset pagination when filters/sort change
    useEffect(() => {
        setVisibleCount(6);
    }, [filterBy, sortBy]);

    const isNewJob = (dateStr) => {
        if (!dateStr) return false;
        const created = new Date(dateStr);
        const diffDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
        return diffDays <= 7; // NEW for 7 days
    };

    const getSalaryRange = (job) => {
        const min = Number(job.salary_min_amount || 0);
        const max = Number(job.salary_max_amount || 0);
        if (!min || !max) return null;
        return {
            text: `₹${min.toLocaleString()} - ₹${max.toLocaleString()}${job.salary_per_type ? `/${job.salary_per_type}` : ""}`,
            min,
            max,
        };
    };

    // Filtered list
    const filteredJobs = useMemo(() => {
        let list = Array.isArray(jobs) ? jobs : [];
        if (filterBy === "saved") {
            list = list.filter((j) => bookmarkedJobs[j.uuid]);
        } else if (filterBy === "applied") {
            list = list.filter((j) => appliedJobs[j.uuid]);
        }
        return list;
    }, [jobs, filterBy, bookmarkedJobs, appliedJobs]);

    // Sorted list
    const sortedJobs = useMemo(() => {
        const byDate = (a, b) =>
            new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);

        if (sortBy === "recent") {
            return [...filteredJobs].sort(byDate);
        }
        if (sortBy === "salaryAsc") {
            return [...filteredJobs].sort((a, b) => {
                const aSal = getSalaryRange(a)?.min || 0;
                const bSal = getSalaryRange(b)?.min || 0;
                return aSal - bSal;
            });
        }
        if (sortBy === "salaryDesc") {
            return [...filteredJobs].sort((a, b) => {
                const aSal = getSalaryRange(a)?.min || 0;
                const bSal = getSalaryRange(b)?.min || 0;
                return bSal - aSal;
            });
        }
        return filteredJobs;
    }, [filteredJobs, sortBy]);

    const jobsToShow = useMemo(() => sortedJobs.slice(0, visibleCount), [sortedJobs, visibleCount]);

    const handleBookmarkClick = async (jobId) => {
        try {
            const role = await new Promise((resolve) => {
                fetchUserRole((r) => resolve(r));
            });

            if (role !== "candidate") {
                toast({
                    title: "Please login first",
                    description: "You need to login as a candidate to save or apply for a job.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
                navigate("/login");
                return;
            }

            const isCurrentlyBookmarked = !!bookmarkedJobs[jobId];
            const newStatus = isCurrentlyBookmarked ? "remove" : "save";

            // Optimistic UI
            setBookmarkedJobs((prev) => ({ ...prev, [jobId]: !isCurrentlyBookmarked }));

            await dispatch(updateBookmarkAsync({ job_uuid: jobId, status: newStatus }));

            toast({
                title: `Job ${newStatus === "save" ? "saved" : "removed"} successfully!`,
                status: "success",
                duration: 2000,
                isClosable: true,
                position: "top-right",
            });
        } catch (error) {
            console.error("Failed to update bookmark:", error);
            // Revert on failure
            setBookmarkedJobs((prev) => ({ ...prev, [jobId]: !prev[jobId] }));
            toast({
                title: "Failed to update bookmark.",
                description: "Something went wrong. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        }
    };

    return (
        <div className="px-4 md:px-4 mx-auto mb-12 sm:p-1">
            {/* Header */}
            <div className="mt-5 mb-3 flex flex-wrap items-center gap-3 sm:justify-between justify-center">
                <div>
                    <h2 className="py-2 font-semibold flex justify-center gap-2 sm:justify-start text-4xl text-gray-900">
                        <span className="text-fontBlue">Latest</span> Job Posts
                    </h2>
                    <h6 className="text-gray-600 font-normal">
                        Know your worth and find the job that qualifies your life
                    </h6>
                    <div className="mt-1 text-sm text-gray-500">
                        {sortedJobs.length} post{sortedJobs.length !== 1 ? "s" : ""}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {/* Sort control */}
                    <div className="bg-gray-100 rounded-lg p-1 flex items-center">
                        <button
                            onClick={() => setSortBy("recent")}
                            className={`px-3 py-1 rounded-md text-sm transition ${sortBy === "recent" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"
                                }`}
                        >
                            Recent
                        </button>
                        <button
                            onClick={() => setSortBy("salaryAsc")}
                            className={`px-3 py-1 rounded-md text-sm transition ${sortBy === "salaryAsc" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"
                                }`}
                        >
                            Salary ↑
                        </button>
                        <button
                            onClick={() => setSortBy("salaryDesc")}
                            className={`px-3 py-1 rounded-md text-sm transition ${sortBy === "salaryDesc" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"
                                }`}
                        >
                            Salary ↓
                        </button>
                    </div>

                    {/* Filter control */}
                    <div className="bg-gray-100 rounded-lg p-1 flex items-center">
                        <button
                            onClick={() => setFilterBy("all")}
                            className={`px-3 py-1 rounded-md text-sm transition ${filterBy === "all" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"
                                }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilterBy("saved")}
                            className={`px-3 py-1 rounded-md text-sm transition ${filterBy === "saved" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"
                                }`}
                        >
                            Saved
                        </button>
                        <button
                            onClick={() => setFilterBy("applied")}
                            className={`px-3 py-1 rounded-md text-sm transition ${filterBy === "applied" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-800"
                                }`}
                        >
                            Applied
                        </button>
                    </div>

                    <Link to={"/jobs"}>
                        <Button variant="ghost" colorScheme="blue" size="sm">
                            Browse all jobs →
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:px-4 px-2">
                {/* Loading skeletons */}
                {isLoading && jobs.length === 0 ? (
                    <div className="col-span-2">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <Box key={index} width="full" padding="6" boxShadow="sm" bg="white" mb="4" borderRadius="xl">
                                <SkeletonCircle size="10" />
                                <SkeletonText mt="4" noOfLines={4} spacing="4" skeletonHeight="2" />
                            </Box>
                        ))}
                    </div>
                ) : sortedJobs.length === 0 ? (
                    <div className="col-span-2">
                        <NoDataFound
                            title="No Job Listings Found"
                            message="We couldn't find any job listings at the moment. Try switching filters or check back later."
                        />
                    </div>
                ) : (
                    <>
                        {jobsToShow.map((job) => {
                            const isApplied = !!appliedJobs[job.uuid];
                            const isSaved = !!bookmarkedJobs[job.uuid];
                            const salary = getSalaryRange(job);

                            return (
                                <div
                                    key={job.uuid}
                                    className="relative bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition group"
                                >
                                    {/* Top-right actions */}
                                    <div className="absolute top-3 right-3 flex items-center gap-2">
                                        {isNewJob(job.published_at || job.created_at) && (
                                            <Badge colorScheme="green" variant="subtle" borderRadius="full">
                                                NEW
                                            </Badge>
                                        )}
                                        <Tooltip label={isSaved ? "Remove from saved" : "Save job"}>
                                            <IconButton
                                                size="sm"
                                                variant="ghost"
                                                aria-label={isSaved ? "Unsave job" : "Save job"}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBookmarkClick(job.uuid);
                                                }}
                                                icon={
                                                    isSaved ? (
                                                        <BookmarkSolid className="w-5 h-5 text-blue-600" />
                                                    ) : (
                                                        <BookmarkOutline className="w-5 h-5 text-gray-600 group-hover:text-gray-800" />
                                                    )
                                                }
                                            />
                                        </Tooltip>
                                    </div>

                                    {/* Clickable header */}
                                    <Link to={`/jobs/${job.uuid}`} aria-label={`View details for ${job.job_title?.title || "Job"}`}>
                                        <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100">
                                            {/* Company Logo */}
                                            <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-50">
                                                {job?.company_logo ? (
                                                    <img
                                                        className="w-full h-full object-cover border rounded-lg"
                                                        src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job?.company_logo}`}
                                                        alt={job.company_name}
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <Avatar name={job.company_name} />
                                                )}
                                            </div>

                                            {/* Job Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-lg text-gray-900 truncate">
                                                    {job.job_title?.title || "N/A"}
                                                </div>
                                                <p className="text-sm text-blue-600 font-medium truncate">{job.job_name || "N/A"}</p>

                                                {/* Badges inline (type/tag) */}
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {job.job_type ? (
                                                        <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                                            {job.job_type}
                                                        </span>
                                                    ) : null}
                                                    {job.job_tag ? (
                                                        <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                                                            {job.job_tag}
                                                        </span>
                                                    ) : null}
                                                    {isApplied ? (
                                                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                                                            <CheckCircleIcon className="w-4 h-4" />
                                                            Applied
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </Link>

                                    {/* Body */}
                                    <div className="px-4 py-2 space-y-1">
                                        {/* Company */}
                                        <div className="flex items-center text-gray-600 text-sm">
                                            <BriefcaseIcon className="h-4 w-4 mr-1" />
                                            <span className="truncate">{job.company_name}</span>
                                        </div>

                                        {/* Location */}
                                        <div className="flex items-center text-gray-600 text-sm">
                                            <MapPinIcon className="h-4 w-4 mr-1" />
                                            <span className="truncate">
                                                {Array.isArray(job.job_locations) &&
                                                    job.job_locations.map((location, index) => (
                                                        <span key={location.uuid || index}>
                                                            {location.job_location?.title}
                                                            {index < job.job_locations.length - 1 ? ", " : ""}
                                                        </span>
                                                    ))}
                                                {job.state?.name ? `, ${job.state?.name}` : ""}
                                            </span>
                                        </div>

                                        {/* Salary */}
                                        <div className="flex items-center text-gray-600 text-sm">
                                            <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
                                            <span>{salary ? salary.text : "Salary Not Disclosed"}</span>
                                        </div>

                                        {/* Skills */}
                                        {Array.isArray(job.job_skills) && job.job_skills.length > 0 && (
                                            <div className="flex items-start text-gray-600 text-sm">
                                                <SparklesIcon className="h-4 w-4 mr-1 mt-0.5" />
                                                <div className="flex flex-wrap gap-1">
                                                    {job.job_skills.slice(0, 5).map((skill) => (
                                                        <span
                                                            key={skill.uuid}
                                                            className="inline-block bg-blue-50 text-blue-800 text-xs px-2 py-1 rounded-full"
                                                        >
                                                            {skill.job_skill?.title}
                                                        </span>
                                                    ))}
                                                    {job.job_skills.length > 5 && (
                                                        <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                                                            +{job.job_skills.length - 5} more
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer */}
                                    <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100">
                                        <div className="flex items-center text-gray-500 text-sm">
                                            <ClockIcon className="h-4 w-4 mr-1" />
                                            <span>
                                                <TimeDifference dateTimeString={job.published_at || job.created_at} />
                                            </span>
                                        </div>

                                        <Button size="sm" colorScheme="green" variant="solid" as={Link} to={`/jobs/${job.uuid}`}>
                                            View Details
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Show more */}
                        {sortedJobs.length > visibleCount && (
                            <div className="col-span-2 flex justify-center mt-2">
                                <Button size="sm" variant="outline" onClick={() => setVisibleCount((c) => c + 6)}>
                                    Show more
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CandidateMostActiveRecentJob;